import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Separator,
  Stack,
  VStack
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import RadioPills, {
  RadioPillOption
} from "@pathwright/ux/src/components/buttons/RadioPills"
import Link, {
  LinkProps,
  LinkTo
} from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { AnimatePresence, motion } from "framer-motion"
import { Fragment, MouseEvent, useContext, useMemo } from "react"
import { Route, Switch, useHistory, useLocation } from "react-router-dom"
import useIntercomAugmentationToolbar from "../../intercom/augmentation/useIntercomAugmentationToolbar"
import Share from "../../share/Share"
import { CoverPageWrapper } from "../CoverPage"
import {
  CloseButton,
  IMMERSIVE_HEADER_HEIGHT,
  ResourceImageCollapsed
} from "../CoverPageHeader"
import { useCoverPageContext } from "../CoverPageProvider"
import { ANIMATION_DURATION, EASE_BEZIER } from "../animations"
import { ImmersiveRoute } from "../types"
import {
  getNextAndPrevItems,
  useCoverPageTranslate,
  useGetPathUrl
} from "../utils"
import {
  CoverPagePathContext,
  ImmersiveRouteConfig,
  getImmersiveRouteForMatch,
  immersiveRoutes,
  joinImmersivePath
} from "./immersiveRouteConfig"

export const ImmersiveTriggerButton = (linkProps: LinkProps) => {
  const { t } = useCoverPageTranslate()

  return (
    <Button
      asChild
      // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
      variant="blurredLightGray"
      py={1}
      px={2}
      h="auto"
    >
      <Link unstyled {...linkProps}>
        <Pathicon icon="cards-fill" />
        {t("immersive.open")}
      </Link>
    </Button>
  )
}

export const ImmersiveSliderArrow = ({
  direction,
  ...linkProps
}: {
  direction: "prev" | "next"
} & LinkProps) => {
  const { t } = useCoverPageTranslate()
  const { isMobile, isLoadingImmersiveRoute, setIsLoadingImmersiveRoute } =
    useCoverPageContext()

  return (
    <VStack
      className="ImmersiveSliderArrow"
      h="100%"
      pos="absolute"
      top="0"
      bottom="0"
      align="center"
      justify="center"
      zIndex={1}
      left={direction === "prev" ? { base: "2px", sm: "10px" } : undefined}
      right={direction === "next" ? { base: "2px", sm: "10px" } : undefined}
    >
      <IconButton
        aria-label={
          direction === "prev" ? t("immersive.previous") : t("immersive.next")
        }
        asChild
        // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
        variant="blurredLightGray"
        loading={isLoadingImmersiveRoute}
        disabled={isLoadingImmersiveRoute}
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={(e) => {
          setIsLoadingImmersiveRoute(true)
          linkProps.onClick?.(e as unknown as MouseEvent<HTMLAnchorElement>)
        }}
        // for some reason, the chevrons appear off centered
        pr={direction === "prev" ? "3px" : undefined}
        pl={direction === "next" ? "3px" : undefined}
        h={{ base: "32px", sm: "40px" }}
        w={{ base: "32px", sm: "40px" }}
        minW={{ base: "32px", sm: "40px" }}
        minH={{ base: "32px", sm: "40px" }}
        borderRadius="full"
        fontSize="sm"
        css={{
          "& svg": {
            fontSize: "inherit"
          }
        }}
      >
        <Link {...linkProps} variant="plain" unstyled={true}>
          <Pathicon
            icon={
              `chevron-${direction === "prev" ? "left" : "right"}` as IconName
            }
            size={isMobile ? "1.4em" : "2em"}
          />
        </Link>
      </IconButton>
    </VStack>
  )
}

const ImmersiveNavigationArrows = () => {
  const history = useHistory()
  const coverPageContext = useCoverPageContext()
  const { getImmersiveRoutePath, immersiveRoute, setIsLoadingImmersiveRoute } =
    coverPageContext

  const activeRoute = immersiveRoutes.find(
    (rc) => rc.key === immersiveRoute?.name
  )

  // All active routes will include:
  // Static routes, when data exists for the route (ex: overview, trailer, contributors, offers)
  // Dynamic routes (single routes for contributors, offers, previews)
  const getActiveImmersiveRoutes = () =>
    immersiveRoutes.reduce<ImmersiveRoute[]>(
      (acc: ImmersiveRoute[], route: ImmersiveRouteConfig) => [
        ...acc,
        ...route.getNavigableImmersiveRoutes(coverPageContext)
      ],
      []
    )

  const navigableRoutes = getActiveImmersiveRoutes()

  const nextAndPrev = getNextAndPrevItems<ImmersiveRoute>(
    navigableRoutes,
    immersiveRoute,
    {
      circular: false,
      getKey: (item) => item.id || item.name
    }
  )

  const handlePrevKey = () => {
    if (nextAndPrev?.prevItem) {
      setIsLoadingImmersiveRoute(true)
      const path = getImmersiveRoutePath?.(nextAndPrev.prevItem)
      if (path) history.push(path)
    }
  }

  const handleNextKey = () => {
    if (nextAndPrev?.nextItem) {
      setIsLoadingImmersiveRoute(true)
      const path = getImmersiveRoutePath?.(nextAndPrev.nextItem)
      if (path) history.push(path)
    }
  }

  useKeyControl("ArrowLeft", handlePrevKey, { ignoreEditableTarget: true })
  useKeyControl("ArrowRight", handleNextKey, { ignoreEditableTarget: true })

  if (!activeRoute || !immersiveRoute) return null

  return (
    <Box
      className="ImmersiveNavigationArrows"
      pos="absolute"
      top="50%"
      transform="translateY(-50%)"
      left="0"
      right="0"
      zIndex={1}
      px={{ base: 0, sm: 2 }}
    >
      <HStack
        justify="space-between"
        align="center"
        w="100%"
        // maxW="1100px"
        mx="auto"
        position="relative"
      >
        {nextAndPrev?.prevItem && (
          <ImmersiveSliderArrow
            direction="prev"
            to={getImmersiveRoutePath?.(nextAndPrev.prevItem) || ""}
          />
        )}
        {nextAndPrev?.nextItem && (
          <ImmersiveSliderArrow
            direction="next"
            to={getImmersiveRoutePath?.(nextAndPrev.nextItem) || ""}
          />
        )}
      </HStack>
    </Box>
  )
}

const mobileBarButtonSize = { base: "36px", sm: "40px", md: "auto" }

// On mobile, we hide the augmentation toolbar
// and show the toolbar options in the navigation bar
const ImmersiveNavToolbar = () => {
  const { t } = useCoverPageTranslate()
  const resource = useCoverPageContext((v) => v.resource)

  const pathUrl = useGetPathUrl()
  const intercomOption = useIntercomAugmentationToolbar()

  const toolbarButtonStyles = {
    bgColor: "transparent",
    _hover: {
      bgColor: "transparent"
    },
    minW: "0",
    cursor: "pointer",
    px: 2,
    h: mobileBarButtonSize,
    w: mobileBarButtonSize
  }

  return (
    <>
      <Separator
        orientation="vertical"
        borderColor="whiteAlpha.900"
        h="100%"
        minH="20px"
        mx={1}
      />
      <HStack align="center" gap={0} pl={0} pr={0.5}>
        <Share
          context={{
            type: "cohort",
            typeId: resource?.curriculum_group?.id
          }}
          hideTabs
          openWithKeys
          onToggleIsOpen={null}
          placement={null}
          triggerLabel={null}
          portal={false}
          displayType="modal"
        >
          {({ renderTrigger }) =>
            renderTrigger(({ onToggle }: { onToggle: () => void }) => (
              <IconButton
                aria-label={t("immersive.share")}
                onClick={onToggle}
                display="flex"
                alignItems="center"
                justifyContent="center"
                children={<Pathicon icon="share-fill" color="white" />}
                {...toolbarButtonStyles}
              />
            ))
          }
        </Share>
        {pathUrl && (
          <Link
            aria-label={t("immersive.open_path")}
            as={IconButton}
            to={pathUrl as LinkTo}
            // Link doesn't accept the icon prop, so we should remove?
            // icon={<Pathicon icon="path" color="white" />}
            {...toolbarButtonStyles}
          />
        )}
        {intercomOption && (
          <IconButton
            aria-label={t("immersive.get_help")}
            onClick={intercomOption.onClick}
            children={<Pathicon icon={intercomOption.icon} color="white" />}
            {...toolbarButtonStyles}
          />
        )}
      </HStack>
    </>
  )
}

// Shows a navigation bar at the bottom of the immersive content
// with links to the different immersive routes
// Not all routes have a link (see displayAsNavLabel);
// some routes without a link, when active
//  will highlight a different (parent) route (ex: offer -> offers)
const ImmersiveNavigationBar = () => {
  const { t } = useCoverPageTranslate()
  const history = useHistory()
  const coverPageContext = useCoverPageContext()
  const {
    getImmersiveRoutePath,
    isMobile,
    immersiveRoute,
    immersiveActive,
    setIsLoadingImmersiveRoute
  } = coverPageContext

  const { navItems, activeItemKey, options } = useMemo(() => {
    // 1) Get routes visible in the nav
    const visible = immersiveRoutes.filter((r) => {
      const show =
        r.displayAsNavLabel === true ||
        (typeof r.displayAsNavLabel === "function" &&
          r.displayAsNavLabel(coverPageContext))
      const renderable = r.shouldRender
        ? r.shouldRender(coverPageContext)
        : true
      return show && renderable
    })

    // 2) Group by navGroup (fallback to key)
    const groupMap = new Map<string, (typeof immersiveRoutes)[number]>()
    for (const r of visible) {
      const g = r.navGroup ?? r.key
      if (!groupMap.has(g)) groupMap.set(g, r)
    }
    const navItems = Array.from(groupMap.entries()).map(
      ([groupKey, config]) => ({
        groupKey,
        config
      })
    )

    // 3) Determine active group (route's navGroup or its own key)
    const activeRoute = immersiveRoutes.find(
      (rc) => rc.key === immersiveRoute?.name
    )
    const activeItemKey = activeRoute?.navGroup ?? activeRoute?.key

    // 4) Options for RadioPills / UI
    let options: RadioPillOption[] = navItems.map(({ config, groupKey }) => ({
      label: isMobile ? (
        config.navIcon ? (
          <Pathicon icon={config.navIcon} />
        ) : null
      ) : (
        (() => {
          if (groupKey === "offers") {
            return (
              <>
                {t("join")} <Pathicon icon="chevron-right" />
              </>
            )
          }
          if (groupKey === "overview") return t("immersive.nav.overview")
          if (groupKey === "trailer") return t("immersive.nav.trailer")
          if (groupKey === "contributors")
            return t("immersive.nav.contributors")
          if (groupKey === "previews") return t("immersive.nav.preview")
          if (groupKey === "more") return t("immersive.nav.more")
          return config.navLabel ?? groupKey
        })()
      ),
      value: groupKey
    }))

    return { navItems, activeItemKey, options }
  }, [coverPageContext, isMobile, immersiveRoute, t])

  const handleChangeNavRoute = (index: number) => {
    const { groupKey, config } = navItems[index] || {}
    if (!config) return

    setIsLoadingImmersiveRoute(true)

    // If this group is a collection (has navigable items), and we're already in it, do nothing
    const items = config.getNavigableImmersiveRoutes?.(coverPageContext) || []
    const isCollection = items.length > 0

    const currentRoute = immersiveRoute
    const currentGroupKey =
      immersiveRoutes.find((rc) => rc.key === currentRoute?.name)?.navGroup ??
      currentRoute?.name

    if (isCollection) {
      if (currentGroupKey === groupKey) {
        // already in this collection; do nothing
        return
      }
      const first = items[0]
      if (first) {
        const path = getImmersiveRoutePath?.(first)
        if (path) history.push(path)
      }
      return
    }

    // Static routes
    const path = getImmersiveRoutePath?.({ name: config.key })
    if (path) history.push(path)
  }

  return (
    <HStack
      className="ImmersiveNavigationBar"
      justify="center"
      pos={"fixed"}
      bottom={0}
      left={0}
      right={0}
      w="full"
      pt={4}
      pb={{ base: "20px", md: 10 }}
      css={{
        "& .RadioPills-highlight-offers": {
          backgroundColor: "transparent"
        },
        "& .RadioPills-radio:not(.RadioPills-divider) .RadioPills-radio-visual":
          {
            paddingX: isMobile ? 2 : 4
          },
        "& .RadioPills-radio-offers .RadioPills-radio-visual": {
          backgroundColor: "whiteAlpha.900",
          color: "brand.600",
          borderRadius: "25px",
          _hover: {
            backgroundColor: "white",
            color: "brand.600"
          }
        },
        "& .RadioPills-radio-offers.radio-pill-active .RadioPills-radio-visual":
          {
            backgroundColor: "brand.600",
            color: "white",
            _hover: {
              backgroundColor: "brand.500",
              color: "white"
            }
          },
        // Hide background because we'll merge Radio pills with toolbar options on mobile
        "& .RadioPills-background": {
          display: "none"
        },
        "& .RadioPills-radio-visual": {
          h: mobileBarButtonSize,
          w: mobileBarButtonSize,
          fontSize: "md"
        }
      }}
    >
      <HStack
        className="ImmersiveNavigationBarContent"
        borderRadius="full"
        bgColor="rgba(153, 153, 153, 0.5)"
        gap={0}
        align="center"
        justify="center"
      >
        <RadioPills
          options={options}
          activeIndex={options.findIndex((r) => r.value === activeItemKey)}
          onChange={handleChangeNavRoute}
          size="md"
        />
        {isMobile && immersiveActive && <ImmersiveNavToolbar />}
      </HStack>
    </HStack>
  )
}

const ImmersiveNavigation = () => {
  return (
    <>
      <ImmersiveNavigationArrows />
      <ImmersiveNavigationBar />
    </>
  )
}

export type ImmersiveHeaderProps = {
  className?: string
  title?: React.ReactNode
  showShare?: boolean
  showClose?: boolean
}

export const ImmersiveHeader = ({ className, title }: ImmersiveHeaderProps) => {
  const resource = useCoverPageContext((v) => v.resource)
  const immersiveBackTo = useCoverPageContext((v) => v.immersiveBackTo)
  const isMobile = useCoverPageContext((v) => v.isMobile)

  const { name } = resource || {}

  const headerClassName = ["ImmersiveHeader", className]
    .filter(Boolean)
    .join(" ")

  return (
    <HStack
      as="header"
      className={headerClassName}
      width="100%"
      maxW={{ base: "100%" }}
      m="0 auto"
      height={{ base: "70px", md: `${IMMERSIVE_HEADER_HEIGHT}px` }}
      align="center"
      gap={{ base: 2, md: 5 }}
      pt={{ base: 2, md: "20px" }}
      pl={"20px"}
      // Larger right padding to account for the augmentation toolbar
      // Toolbar is 44px wide and offset by 20px from the right edge
      pr={{ base: "20px", md: `calc(20px + 44px + 20px)` }}
      zIndex={1}
    >
      {/* Offset the already positioned close button a bit more */}
      {isMobile ? (
        <Box pos="absolute" top={{ base: 0, md: 2 }} right={0}>
          <CloseButton />
        </Box>
      ) : null}
      <ResourceImageCollapsed
        to={immersiveBackTo}
        isActive={true}
        layoutId={"immersive-cover-thumb"}
        fadeInDuration={ANIMATION_DURATION.fade}
        fadeInDelay={0.05}
      />
      <Stack
        flexDirection={"row"}
        justifyContent={{ base: "flex-start", md: "space-between" }}
        alignItems={"center"}
        w="100%"
        gap={2}
        zIndex={1}
        overflowX="hidden"
      >
        <Heading
          asChild
          fontSize={["19px", "22px"]}
          m={0}
          color="white"
          fontWeight="700"
          textShadow="0 1px 5px rgba(0, 0, 0, 0.10)"
          textAlign="left"
          maxW={{ base: "calc(100% - 40px)", md: "100%" }}
          lineHeight="1.2"
          lineClamp={3}
        >
          <motion.h2
            layoutId={"immersive-title"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATION_DURATION.fade,
              ease: EASE_BEZIER,
              delay: 0.1
            }}
          >
            {title ?? name}
          </motion.h2>
        </Heading>
      </Stack>
    </HStack>
  )
}

export const ImmersiveContent = ({
  children,
  className = ""
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { immersiveRoute } = useCoverPageContext()

  return (
    <CoverPageWrapper
      className={`CoverPageImmersive ${className} ${immersiveRoute?.name}`}
      isFullscreen
      css={{
        overflow: "hidden",
        bgColor: "transparent",
        "& .ImmersiveNavigationBar": {
          display: immersiveRoute?.name === "offers" ? "none" : undefined
        }
      }}
    >
      <VStack
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        gap={0}
        // Prevent nested card chrome from showing by occupying full viewport
        css={{
          maxW: "100%",
          maxH: "100%"
        }}
        alignItems="stretch"
        justifyContent="center"
      >
        <ImmersiveHeader />
        <VStack
          className="CoverPageImmersiveContent"
          as="main"
          h="100%"
          w="100%"
          maxH={{ base: "calc(100vh - 70px)", md: "calc(100vh - 94px)" }}
          mx="auto"
          overflowY="hidden"
          color="primary"
          // Allow the content to be full width in immersive mode;
          // each route's component should handle its own max width
          px={{ base: 3, sm: 5 }}
          pt={0}
          pb={{ base: "76px", md: "94px" }}
          align="stretch"
          justify="center"
        >
          {children}
        </VStack>
        <ImmersiveNavigation />
      </VStack>
    </CoverPageWrapper>
  )
}

const CoverPageImmersiveContainer = ({}: {}) => {
  const immersiveRoute = useCoverPageContext((v) => v.immersiveRoute)
  const resource = useCoverPageContext((v) => v.resource)
  const setIsLoadingImmersiveRoute = useCoverPageContext(
    (v) => v.setIsLoadingImmersiveRoute
  )
  const coverPath = useContext(CoverPagePathContext)
  const location = useLocation()

  const immersiveRouteConfig = immersiveRoutes.find(
    (r) => r.key === immersiveRoute?.name
  )

  if (!immersiveRoute || !immersiveRouteConfig) return null

  const className = `CoverPage${
    immersiveRouteConfig.key.charAt(0).toUpperCase() +
    immersiveRouteConfig.key.slice(1)
  }`

  // A unique key ensures Framer Motion will animate the content
  // Routes with no id are singletons and thus are safe keys
  // Keep preview content mounted across preview id changes so shared UI
  // (like preview dot nav) doesn't fade/remount between path/step previews.
  const identityKey =
    immersiveRoute.name === "preview"
      ? immersiveRoute.name
      : immersiveRoute.id
      ? `${immersiveRoute.name}-${immersiveRoute.id}`
      : immersiveRoute.name

  // Each view is a child route of the cover page route. The Switch is given
  // this render's location so a view fading out keeps rendering its own route.
  const RouteComponent = (
    <Fragment key={`${identityKey}-content`}>
      {coverPath ? (
        <Switch location={location}>
          {immersiveRoutes.map((config) => (
            <Route
              key={config.key}
              path={joinImmersivePath(coverPath, config.path)}
              exact
              strict
              render={({ match }) => {
                const route = getImmersiveRouteForMatch(coverPath, match)
                return route ? config.render(route) : null
              }}
            />
          ))}
        </Switch>
      ) : (
        immersiveRouteConfig.render(immersiveRoute)
      )}
    </Fragment>
  )

  return (
    <>
      <Box
        asChild
        position="absolute"
        inset={0}
        zIndex={1}
        borderRadius="0"
        className="ImmersiveBackground"
        css={{
          "& .ResourceBackground": {
            borderRadius: "0"
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: ANIMATION_DURATION.fade, ease: EASE_BEZIER }}
        >
          <ResourceBackground resource={resource} forceBlur />
        </motion.div>
      </Box>
      <ImmersiveContent className={className}>
        <AnimatePresence mode="wait" initial={false}>
          <Box
            asChild
            className="ImmersiveContentWrapper"
            key={identityKey}
            py={0}
            maxH="100%"
            overflowY="auto"
            overflowX="hidden"
            mx="auto"
            w="100%"
            maxW={immersiveRouteConfig?.maxWidth ?? "800px"}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onAnimationComplete={() => {
                setIsLoadingImmersiveRoute(false)
              }}
              transition={{
                duration: ANIMATION_DURATION.fade,
                ease: EASE_BEZIER
              }}
            >
              {RouteComponent}
            </motion.div>
          </Box>
        </AnimatePresence>
      </ImmersiveContent>
    </>
  )
}

export default CoverPageImmersiveContainer
